import React, { Suspense, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    MdArrowBack,
    MdInventory2,
    MdMap,
    MdOutlineAttachMoney,
    MdReceiptLong,
    MdStorefront,
    MdTrendingUp,
} from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import StatCard from './components/StatCard';
import {
    fetchCustomerById,
    fetchCustomerTransactions,
} from '../../../services/Http/Api';
import { useCurrency } from '../../../context/CurrencyProvider';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

const CustomerDetails = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();

    const [buyer, setBuyer] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let mounted = true;

        const toList = (payload) => {
            if (Array.isArray(payload?.data)) return payload.data;
            if (Array.isArray(payload)) return payload;
            return [];
        };

        const loadAllTransactions = async (userId) => {
            const pages = [];
            let page = 1;
            let lastPage = 1;
            const maxPages = 8;

            do {
                const response = await fetchCustomerTransactions(userId, {
                    page,
                    perPage: 100,
                });
                pages.push(...toList(response));
                lastPage = Number(response?.last_page || response?.meta?.last_page || 1);
                page += 1;
            } while (page <= lastPage && page <= maxPages);

            return pages;
        };

        const load = async () => {
            try {
                setLoading(true);
                const buyerData = await fetchCustomerById(customerId);
                const userId = buyerData?.user_id || buyerData?.user?.id;
                if (!userId) {
                    throw new Error('Customer user id missing');
                }

                const transactionsData = await loadAllTransactions(userId);

                if (!mounted) return;
                setBuyer(buyerData);
                setTransactions(transactionsData);
            } catch (err) {
                if (!mounted) return;
                setError(err?.response?.data?.error || err?.message || 'Failed to load customer details');
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            mounted = false;
        };
    }, [customerId]);

    const parsedVenueStats = useMemo(() => {
        const raw = buyer?.venues_statistics;
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        try {
            return JSON.parse(raw);
        } catch (e) {
            return [];
        }
    }, [buyer]);

    const venueNameById = useMemo(() => {
        return transactions.reduce((acc, tx) => {
            const venueId = tx?.seller_venue_id || tx?.venue_id;
            const venueName = tx?.seller_venue_name || tx?.venue_name;
            if (venueId && venueName && !acc[venueId]) {
                acc[venueId] = venueName;
            }
            return acc;
        }, {});
    }, [transactions]);

    const totals = useMemo(() => {
        const count = transactions.length;
        const total = transactions.reduce((sum, tx) => sum + Number(tx?.price || 0), 0);
        return {
            count,
            total,
            avg: count > 0 ? total / count : 0,
            items: Number(buyer?.count_of_items || 0),
            buyerTotal: Number(buyer?.total_spent || 0),
        };
    }, [transactions, buyer]);

    const monthlySeries = useMemo(() => {
        const map = transactions.reduce((acc, tx) => {
            if (!tx?.date_of_transaction) return acc;
            const month = String(tx.date_of_transaction).slice(0, 7);
            if (!acc[month]) {
                acc[month] = { revenue: 0, transactions: 0 };
            }
            acc[month].revenue += Number(tx?.price || 0);
            acc[month].transactions += 1;
            return acc;
        }, {});

        const categories = Object.keys(map).sort();
        return {
            categories,
            revenueData: categories.map((key) => Number(map[key].revenue.toFixed(2))),
            txData: categories.map((key) => map[key].transactions),
        };
    }, [transactions]);

    const itemSummaryRows = useMemo(() => {
        const grouped = transactions.reduce((acc, tx) => {
            const manufacturerItemId = tx?.manufacturer_item_id || 'unknown';
            if (!acc[manufacturerItemId]) {
                acc[manufacturerItemId] = {
                    manufacturer_item_id: manufacturerItemId,
                    item_count: 0,
                    total_spent: 0,
                };
            }
            acc[manufacturerItemId].item_count += 1;
            acc[manufacturerItemId].total_spent += Number(tx?.price || 0);
            return acc;
        }, {});

        return Object.values(grouped)
            .sort((a, b) => b.total_spent - a.total_spent)
            .slice(0, 12)
            .map((row) => ({
                ...row,
                name: row.name
                    || transactions.find((tx) => tx?.manufacturer_item_id === row.manufacturer_item_id)?.manufacturer_item_name
                    || row.manufacturer_item_id,
            }));
    }, [transactions]);

    const transactionColumns = [
        {
            name: t('Users.CustomerDetails.columns.date', 'Date'),
            selector: (row) => row?.date_of_transaction,
            sortable: true,
            cell: (row) => row?.date_of_transaction
                ? new Date(row.date_of_transaction).toLocaleDateString()
                : 'N/A',
        },
        {
            name: t('Users.CustomerDetails.columns.venue', 'Venue'),
            selector: (row) => row?.seller_venue_id || '',
            sortable: false,
            cell: (row) => row?.seller_venue_name || venueNameById[row?.seller_venue_id || row?.venue_id] || row?.seller_venue_id || row?.venue_id || 'N/A',
        },
        {
            name: t('Users.CustomerDetails.columns.item', 'Item'),
            selector: (row) => row?.manufacturer_item_id || '',
            sortable: false,
            cell: (row) => row?.manufacturer_item_name || row?.manufacturer_item_id || 'N/A',
        },
        {
            name: t('Users.CustomerDetails.columns.amount', 'Amount'),
            selector: (row) => Number(row?.price || 0),
            sortable: true,
            cell: (row) => formatResolvedAmount({
                eurAmount: row?.price || 0,
                localAmount: row?.price_seller_local ?? row?.price_buyer_local,
                localCurrencyId: row?.seller_currency_id ?? row?.buyer_currency_id,
            }),
        },
    ];

    const itemColumns = [
        {
            name: t('Users.CustomerDetails.items.product', 'Product'),
            selector: (row) => row?.name || '',
            sortable: true,
        },
        {
            name: t('Users.CustomerDetails.items.count', 'Transactions'),
            selector: (row) => row?.item_count || 0,
            sortable: true,
        },
        {
            name: t('Users.CustomerDetails.items.value', 'Total Spent'),
            selector: (row) => row?.total_spent || 0,
            sortable: true,
            cell: (row) => formatResolvedAmount({ eurAmount: row?.total_spent || 0 }),
        },
    ];

    const venueColumns = [
        {
            name: t('Users.CustomerDetails.venues.venue', 'Venue'),
            selector: (row) => row?.venue_id || '',
            cell: (row) => {
                const statName = row?.venue_name || row?.name || row?.venue?.name || null;
                const mappedName = venueNameById[row?.venue_id];
                return statName || mappedName || row?.venue_id || 'N/A';
            },
        },
        {
            name: t('Users.CustomerDetails.venues.items', 'Items'),
            selector: (row) => Number(row?.total_items || 0),
            sortable: true,
        },
        {
            name: t('Users.CustomerDetails.venues.spent', 'Spent'),
            selector: (row) => Number(row?.total_spent || 0),
            sortable: true,
            cell: (row) => formatResolvedAmount({ eurAmount: row?.total_spent || 0 }),
        },
        {
            name: t('Users.CustomerDetails.venues.lastPurchase', 'Last Purchase'),
            selector: (row) => row?.last_transaction_date || '',
            cell: (row) => row?.last_transaction_date
                ? new Date(row.last_transaction_date).toLocaleDateString()
                : 'N/A',
        },
    ];

    const chartOptions = {
        chart: {
            type: 'line',
            height: 320,
            toolbar: { show: false },
        },
        stroke: { curve: 'smooth', width: [3, 3] },
        colors: ['#821692', '#3fc380'],
        xaxis: {
            categories: monthlySeries.categories,
            labels: { rotate: -35 },
        },
        yaxis: [
            {
                title: { text: t('Users.CustomerDetails.chart.revenue', 'Revenue') },
                labels: {
                    formatter: (v) => Number(v).toFixed(0),
                },
            },
            {
                opposite: true,
                title: { text: t('Users.CustomerDetails.chart.transactions', 'Transactions') },
            },
        ],
        legend: { position: 'top', horizontalAlign: 'right' },
        dataLabels: { enabled: false },
    };

    const chartSeries = [
        { name: t('Users.CustomerDetails.chart.revenue', 'Revenue'), type: 'line', data: monthlySeries.revenueData },
        { name: t('Users.CustomerDetails.chart.transactions', 'Transactions'), type: 'line', data: monthlySeries.txData },
    ];

    if (loading) {
        return <LoadingMessage>{t('Users.CustomerDetails.loading', 'Loading customer details...')}</LoadingMessage>;
    }

    if (error || !buyer) {
        return <ErrorMessage>{error || t('Users.CustomerDetails.error', 'Failed to load customer details.')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <BackButton onClick={() => navigate('/customers')}>
                    <MdArrowBack />
                    {t('Users.CustomerDetails.back', 'Back to Customers')}
                </BackButton>
                <PageTitle icon={<MdStorefront size={28} />}>
                    {buyer?.user_name} {buyer?.user_surname}
                </PageTitle>
            </PageHeader>

            <PageContent>
                <InfoRow>
                    <InfoCard>
                        <InfoTitle>{t('Users.CustomerDetails.identity', 'Customer')}</InfoTitle>
                        <InfoValue>{buyer?.user_name} {buyer?.user_surname}</InfoValue>
                        <InfoMuted>ID: {buyer?.user_id}</InfoMuted>
                        <StatusPill $own={Boolean(buyer?.own_buyer)}>
                            {buyer?.own_buyer
                                ? t('Users.UsersList.ownUser', 'Own Customer')
                                : t('Users.UsersList.globalUser', 'Global Customer')}
                        </StatusPill>
                    </InfoCard>
                    <InfoCard>
                        <InfoTitle>{t('Users.CustomerDetails.organizationScope', 'Organization Scope')}</InfoTitle>
                        <InfoMuted>{t('Users.CustomerDetails.scopeHint', 'Data shown only for this organization and its venues.')}</InfoMuted>
                        <InfoMuted>
                            {t('Users.CustomerDetails.firstPurchase', 'First Purchase')}: {buyer?.first_transaction_date
                                ? new Date(buyer.first_transaction_date).toLocaleDateString()
                                : 'N/A'}
                        </InfoMuted>
                        <InfoMuted>
                            {t('Users.CustomerDetails.lastPurchase', 'Last Purchase')}: {buyer?.last_transaction_date
                                ? new Date(buyer.last_transaction_date).toLocaleDateString()
                                : 'N/A'}
                        </InfoMuted>
                    </InfoCard>
                </InfoRow>

                <StatsGrid>
                    <StatCard
                        icon={<MdOutlineAttachMoney className="icon" />}
                        title={t('Users.CustomerDetails.cards.totalSpent', 'Total Spent')}
                        value={formatResolvedAmount({ eurAmount: totals.buyerTotal || totals.total })}
                        color="totalVolume"
                    />
                    <StatCard
                        icon={<MdInventory2 className="icon" />}
                        title={t('Users.CustomerDetails.cards.itemsBought', 'Items Bought')}
                        value={totals.items.toLocaleString()}
                        color="users"
                    />
                    <StatCard
                        icon={<MdReceiptLong className="icon" />}
                        title={t('Users.CustomerDetails.cards.transactions', 'Transactions')}
                        value={totals.count.toLocaleString()}
                        color="transactions"
                    />
                    <StatCard
                        icon={<MdTrendingUp className="icon" />}
                        title={t('Users.CustomerDetails.cards.avgTicket', 'Average Ticket')}
                        value={formatResolvedAmount({ eurAmount: totals.avg })}
                        color="venues"
                    />
                </StatsGrid>

                <StyledCard>
                    <SectionTitle>{t('Users.CustomerDetails.chart.title', 'Spending Trend Over Time')}</SectionTitle>
                    <Suspense fallback={<LoadingMessage>{t('Users.CustomerDetails.chartLoading', 'Loading chart...')}</LoadingMessage>}>
                        <ReactApexChart options={chartOptions} series={chartSeries} type="line" height={320} />
                    </Suspense>
                </StyledCard>

                <Grid2>
                    <StyledCard noPadding>
                        <SectionTitleWrap>
                            <MdInventory2 />
                            <SectionTitle>{t('Users.CustomerDetails.items.title', 'Bought Items (Organization)')}</SectionTitle>
                        </SectionTitleWrap>
                        <DataTable
                            columns={itemColumns}
                            data={itemSummaryRows}
                            noDataComponent={t('Users.CustomerDetails.items.empty', 'No item data available')}
                            dense
                        />
                    </StyledCard>

                    <StyledCard noPadding>
                        <SectionTitleWrap>
                            <MdMap />
                            <SectionTitle>{t('Users.CustomerDetails.venues.title', 'Favorite Venues')}</SectionTitle>
                        </SectionTitleWrap>
                        <DataTable
                            columns={venueColumns}
                            data={parsedVenueStats}
                            noDataComponent={t('Users.CustomerDetails.venues.empty', 'No venue statistics available')}
                            dense
                        />
                    </StyledCard>
                </Grid2>

                <StyledCard noPadding>
                    <SectionTitleWrap>
                        <MdReceiptLong />
                        <SectionTitle>{t('Users.CustomerDetails.transactions.title', 'Transactions')}</SectionTitle>
                    </SectionTitleWrap>
                    <DataTable
                        columns={transactionColumns}
                        data={transactions}
                        pagination
                        paginationPerPage={10}
                        noDataComponent={t('Users.CustomerDetails.transactions.empty', 'No transactions available')}
                        dense
                    />
                </StyledCard>
            </PageContent>
        </PageContainer>
    );
};

export default CustomerDetails;

const BackButton = styled.button`
    border: none;
    background: none;
    color: var(--color-primary);
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    margin-bottom: 0.5rem;
`;

const InfoRow = styled.div`
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    margin-bottom: 1.25rem;
`;

const InfoCard = styled(StyledCard)`
    margin-bottom: 0;
`;

const InfoTitle = styled.div`
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    margin-bottom: 0.5rem;
`;

const InfoValue = styled.div`
    font-size: 1.25rem;
    font-weight: 700;
    color: #1f2937;
`;

const InfoMuted = styled.div`
    color: #6b7280;
    font-size: 0.875rem;
    margin-top: 0.25rem;
`;

const StatusPill = styled.span`
    display: inline-block;
    margin-top: 0.625rem;
    padding: 0.375rem 0.625rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    background: ${({ $own }) => ($own ? 'rgba(63, 195, 128, 0.15)' : 'rgba(130, 22, 146, 0.12)')};
    color: ${({ $own }) => ($own ? '#1f9d5a' : '#821692')};
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
    margin-bottom: 1.25rem;
`;

const Grid2 = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
`;

const SectionTitleWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 1rem 0 1rem;
    color: var(--color-primary);
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 1rem;
`;
